import { useEffect, useRef, useState } from 'react';
import {
  getEmbeddedFirmwareFiles,
  importEmbeddedFirmware,
  Result,
  inspectEmbeddedFirmware,
} from '../../api/api';
import {
  IEmbeddedFirmwareFile,
  IEmbeddedFirmwareListing,
  IFirmwareFileInspection,
} from '../../api/types';
import { getFirmwareFamily } from '../../api/rules';
import { Button } from '../Common/Button';
import { getDeviceType } from '../../Types';
import { FirmwareInspectionPanel } from './FirmwareInspectionPanel';

const maximumFirmwareSize = 50_000_000;

const emptyListing: IEmbeddedFirmwareListing = {
  files: [],
  folders: [],
  continuationToken: null,
};

type EmbeddedFirmwareListingState = {
  requestKey: string;
  listing: IEmbeddedFirmwareListing;
  error: string;
};

type EmbeddedFirmwareBrowserProps = {
  productCode: string;
  variant: string;
  succesfullyUploadedCallback: () => void;
};

function getErrorMessage(result: Result<unknown>, fallback: string) {
  if (result.status === 'Forbidden') {
    return "You don't have permission to import firmware.";
  }
  return 'errorMessages' in result
    ? result.errorMessages.join(', ')
    : fallback;
}

function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getParentPrefix(prefix: string) {
  const trimmedPrefix = prefix.replace(/\/$/, '');
  const lastSeparator = trimmedPrefix.lastIndexOf('/');
  return lastSeparator < 0
    ? ''
    : `${trimmedPrefix.slice(0, lastSeparator + 1)}`;
}

function getBreadcrumbs(prefix: string) {
  const segments = prefix.split('/').filter(Boolean);
  return segments.map((segment, index) => ({
    label: segment,
    prefix: `${segments.slice(0, index + 1).join('/')}/`,
  }));
}

function FolderIcon({ className }: { className: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        d="M3.75 6.75A1.75 1.75 0 0 1 5.5 5h4l2 2h7A1.75 1.75 0 0 1 20.25 8.75v8.5A1.75 1.75 0 0 1 18.5 19h-13a1.75 1.75 0 0 1-1.75-1.75v-10.5Z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function FileIcon({ className }: { className: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        d="M6.75 3.75h6.5l4 4v12.5H6.75V3.75Z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
      <path
        d="M13.25 3.75v4h4M9.25 12h5.5M9.25 15.5h5.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        d="m9 5 7 7-7 7"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

export const EmbeddedFirmwareBrowser = ({
  productCode,
  variant,
  succesfullyUploadedCallback,
}: EmbeddedFirmwareBrowserProps) => {
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [listingRefreshCount, setListingRefreshCount] = useState(0);
  const listingRequestKey = JSON.stringify([
    productCode,
    variant,
    currentPrefix,
    listingRefreshCount,
  ]);
  const [listingState, setListingState] =
    useState<EmbeddedFirmwareListingState>({
      requestKey: '',
      listing: emptyListing,
      error: '',
    });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const listing =
    listingState.requestKey === listingRequestKey
      ? listingState.listing
      : emptyListing;
  const isListing =
    listingState.requestKey !== listingRequestKey || isLoadingMore;
  const listingError =
    listingState.requestKey === listingRequestKey
      ? listingState.error
      : '';
  const [search, setSearch] = useState('');
  const [selectedFile, setSelectedFile] =
    useState<IEmbeddedFirmwareFile | null>(null);
  const [inspection, setInspection] =
    useState<IFirmwareFileInspection | null>(null);
  const [metadataMessage, setMetadataMessage] = useState('');
  const [firmwareNumber, setFirmwareNumber] = useState('');
  const [firmwareSemantic, setFirmwareSemantic] = useState('');
  const [useFirmwareFamily, setUseFirmwareFamily] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<
    'idle' | 'success' | 'fail'
  >('idle');
  const [responseMessage, setResponseMessage] = useState('');
  const inspectionRequestId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    getEmbeddedFirmwareFiles(
      productCode,
      variant,
      currentPrefix,
    ).then((result) => {
      if (cancelled) return;
      if (result.status === 'OK') {
        setListingState({
          requestKey: listingRequestKey,
          listing: result.data,
          error: '',
        });
      } else {
        setListingState({
          requestKey: listingRequestKey,
          listing: emptyListing,
          error: getErrorMessage(
            result,
            'Could not load embedded firmware.',
          ),
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [productCode, variant, currentPrefix, listingRequestKey]);

  const resetSelection = () => {
    inspectionRequestId.current++;
    setSelectedFile(null);
    setInspection(null);
    setMetadataMessage('');
    setFirmwareNumber('');
    setFirmwareSemantic('');
    setResponseMessage('');
    setSubmissionStatus('idle');
  };

  async function loadMore() {
    const continuationToken = listing.continuationToken;
    if (!continuationToken || isListing) return;
    const requestKey = listingRequestKey;
    setIsLoadingMore(true);
    try {
      const result = await getEmbeddedFirmwareFiles(
        productCode,
        variant,
        currentPrefix,
        continuationToken,
      );
      if (result.status === 'OK') {
        setListingState((current) => {
          if (current.requestKey !== requestKey) return current;
          return {
            ...current,
            listing: {
              files: [...current.listing.files, ...result.data.files],
              folders: [
                ...new Set([
                  ...current.listing.folders,
                  ...result.data.folders,
                ]),
              ],
              continuationToken: result.data.continuationToken,
            },
            error: '',
          };
        });
      } else {
        setListingState((current) =>
          current.requestKey === requestKey
            ? {
                ...current,
                error: getErrorMessage(
                  result,
                  'Could not load more embedded firmware.',
                ),
              }
            : current,
        );
      }
    } finally {
      setIsLoadingMore(false);
    }
  }

  async function selectFile(file: IEmbeddedFirmwareFile) {
    const requestId = ++inspectionRequestId.current;
    setSelectedFile(file);
    setInspection(null);
    setMetadataMessage('Inspecting firmware file...');
    setFirmwareNumber('');
    setFirmwareSemantic('');
    setResponseMessage('');
    setSubmissionStatus('idle');

    const result = await inspectEmbeddedFirmware(
      productCode,
      variant,
      file.key,
      file.etag,
    );
    if (requestId !== inspectionRequestId.current) return;

    if (result.status === 'OK') {
      setInspection(result.data);
      setFirmwareNumber(
        result.data.suggestedFirmwareVersion?.toString() || '',
      );
      setFirmwareSemantic(result.data.suggestedSemanticVersion || '');
      setMetadataMessage(
        `Detected ${result.data.format}. Values used by FWM remain editable.`,
      );
    } else {
      setMetadataMessage(
        getErrorMessage(
          result,
          'The file could not be inspected. Enter the FWM release values manually.',
        ),
      );
    }
  }

  function inputValidationFeedback() {
    if (!selectedFile) return 'Please choose a firmware file';
    if (!firmwareNumber || !firmwareSemantic)
      return 'Enter both FWM release values';
    if (!/^[1-9]\d*$/.test(firmwareNumber))
      return 'Invalid firmware version number';
    if (firmwareSemantic.startsWith('v'))
      return 'Remove the v prefix from the semantic version';
    if (
      !/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(-[a-zA-Z]+\d*)?$/.test(
        firmwareSemantic,
      )
    )
      return 'Invalid semantic version';
    return 'OK';
  }

  const hasMetadataOverride =
    inspection !== null &&
    ((inspection.suggestedFirmwareVersion !== null &&
      firmwareNumber !== '' &&
      Number(firmwareNumber) !==
        inspection.suggestedFirmwareVersion) ||
      (inspection.suggestedSemanticVersion !== null &&
        firmwareSemantic !== '' &&
        firmwareSemantic !== inspection.suggestedSemanticVersion));

  async function handleImport() {
    if (
      !selectedFile ||
      inputValidationFeedback() !== 'OK' ||
      isImporting
    )
      return;

    setIsImporting(true);
    setResponseMessage('Importing firmware...');
    const productsReceivingImport = useFirmwareFamily
      ? getFirmwareFamily({ productCode, variant })
      : [{ productCode, variant }];

    try {
      const results = await Promise.all(
        productsReceivingImport.map((product) =>
          importEmbeddedFirmware(
            product.productCode,
            product.variant,
            selectedFile.key,
            selectedFile.etag,
            Number(firmwareNumber),
            firmwareSemantic,
          ),
        ),
      );
      const failedResult = results.find(
        (result) => result.status !== 'OK',
      );
      if (failedResult) {
        setSubmissionStatus('fail');
        setResponseMessage(
          getErrorMessage(failedResult, 'Something went wrong.'),
        );
        return;
      }

      setSubmissionStatus('success');
      setResponseMessage('Import Success!');
      succesfullyUploadedCallback();
    } catch {
      setSubmissionStatus('fail');
      setResponseMessage(
        'Could not import firmware. Please try again.',
      );
    } finally {
      setIsImporting(false);
    }
  }

  const filteredFiles = listing.files.filter((file) =>
    file.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const breadcrumbs = getBreadcrumbs(currentPrefix);

  return (
    <div className="file-uploader flex flex-col space-y-3 mt-2">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-blue-50 p-2 text-blue-700">
          <FolderIcon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="w-full font-bold text-xl">
            Embedded firmware from S3
          </h1>
          <p className="text-sm text-gray-600">
            Browse the firmware files stored in S3. Select a .dat file
            to inspect it and import it into FWM.
          </p>
        </div>
      </div>
      <div className="flex flex-row flex-wrap gap-x-7 gap-y-2 text-sm">
        <span>
          Device
          <br />
          <b>{getDeviceType(productCode)}</b>
        </span>
        <span>
          Product code
          <br />
          <b>{productCode}</b>
        </span>
        <span>
          Variant
          <br />
          <b>{variant}</b>
        </span>
      </div>
      {listingError && (
        <p role="alert" className="text-red-600">
          {listingError}
        </p>
      )}
      <section
        aria-label="Embedded firmware file explorer"
        className="overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm"
      >
        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-semibold">File explorer</h2>
            <p className="text-sm text-gray-600">
              Choose a folder or select a firmware file to continue.
            </p>
          </div>
          <span className="text-sm text-gray-500">
            {listing.folders.length}{' '}
            {listing.folders.length === 1 ? 'folder' : 'folders'} ·{' '}
            {filteredFiles.length}{' '}
            {filteredFiles.length === 1 ? 'file' : 'files'}
          </span>
        </div>
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4">
          <nav
            aria-label="Embedded firmware location"
            className="overflow-x-auto"
          >
            <ol className="flex min-w-max items-center gap-1 text-sm">
              <li className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Open folder chargeberry-firmware"
                  aria-current={
                    currentPrefix === '' ? 'page' : undefined
                  }
                  onClick={() => {
                    if (currentPrefix === '') return;
                    resetSelection();
                    setSearch('');
                    setCurrentPrefix('');
                  }}
                  className={`rounded px-1.5 py-1 font-medium ${
                    currentPrefix === ''
                      ? 'text-gray-700'
                      : 'text-blue-700 hover:bg-blue-50 hover:underline'
                  }`}
                >
                  chargeberry-firmware
                </button>
                {breadcrumbs.length > 0 && <ChevronRightIcon />}
              </li>
              {breadcrumbs.map((breadcrumb, index) => {
                const isCurrent = index === breadcrumbs.length - 1;
                return (
                  <li
                    className="flex items-center gap-1"
                    key={breadcrumb.prefix}
                  >
                    {isCurrent ? (
                      <span
                        aria-current="page"
                        className="rounded px-1.5 py-1 font-medium text-gray-700"
                      >
                        {breadcrumb.label}
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Open folder ${breadcrumb.label}`}
                        onClick={() => {
                          resetSelection();
                          setSearch('');
                          setCurrentPrefix(breadcrumb.prefix);
                        }}
                        className="rounded px-1.5 py-1 font-medium text-blue-700 hover:bg-blue-50 hover:underline"
                      >
                        {breadcrumb.label}
                      </button>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRightIcon />
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
          <div className="flex flex-row flex-wrap items-end gap-3">
            <Button
              onClick={() => {
                resetSelection();
                setSearch('');
                setCurrentPrefix(getParentPrefix(currentPrefix));
              }}
              text="Up one folder"
              disabled={[
                {
                  disabled: currentPrefix === '' || isListing,
                  reason: 'Already at the root folder',
                },
              ]}
            />
            <Button
              onClick={() => {
                resetSelection();
                setSearch('');
                setListingRefreshCount((count) => count + 1);
              }}
              text="Refresh folder"
              disabled={[
                {
                  disabled: isListing,
                  reason: 'Loading firmware files...',
                },
              ]}
            />
            <label
              className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm"
              htmlFor="embedded-firmware-search"
            >
              Filter files in this folder
              <input
                id="embedded-firmware-search"
                type="search"
                placeholder="Filter by filename"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="form-control rounded border border-gray-300 px-3 py-1.5"
              />
            </label>
          </div>
        </div>
        {isListing && (
          <p className="border-b border-gray-200 px-4 py-3 text-sm text-gray-500">
            Loading embedded firmware...
          </p>
        )}
        <div
          aria-label="Files and folders"
          className="flex flex-col divide-y divide-gray-200"
        >
          <div className="hidden grid-cols-[minmax(0,1fr)_12rem_6rem] gap-3 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 md:grid">
            <span>Name</span>
            <span>Last modified</span>
            <span className="text-right">Size</span>
          </div>
          {listing.folders.map((folder) => (
            <button
              type="button"
              key={folder}
              aria-label={`Open folder ${folder}`}
              onClick={() => {
                resetSelection();
                setSearch('');
                setCurrentPrefix(`${currentPrefix}${folder}`);
              }}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left hover:bg-blue-50"
            >
              <span className="flex min-w-0 items-center gap-3">
                <FolderIcon className="h-5 w-5 shrink-0 text-amber-500" />
                <span className="truncate font-medium text-gray-700">
                  {folder}
                </span>
              </span>
              <ChevronRightIcon />
            </button>
          ))}
          {filteredFiles.map((file) => {
            const isTooLarge = file.size >= maximumFirmwareSize;
            return (
              <button
                type="button"
                key={file.key}
                aria-label={`Select ${file.name}`}
                aria-pressed={selectedFile?.key === file.key}
                disabled={isTooLarge}
                onClick={() => void selectFile(file)}
                className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left md:grid-cols-[minmax(0,1fr)_12rem_6rem] ${
                  selectedFile?.key === file.key
                    ? 'bg-blue-50 ring-2 ring-inset ring-blue-500'
                    : 'hover:bg-blue-50'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <FileIcon className="h-5 w-5 shrink-0 text-blue-600" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-gray-700">
                      {file.name}
                    </span>
                    <span className="block truncate text-xs text-gray-500 md:hidden">
                      {isTooLarge
                        ? 'Exceeds 50 MB limit'
                        : new Date(
                            file.lastModified,
                          ).toLocaleString()}
                    </span>
                  </span>
                </span>
                <span className="hidden text-sm text-gray-500 md:block">
                  {new Date(file.lastModified).toLocaleString()}
                </span>
                <span className="text-right text-sm text-gray-500">
                  {isTooLarge
                    ? 'Too large'
                    : formatFileSize(file.size)}
                </span>
              </button>
            );
          })}
          {!isListing &&
            listing.folders.length === 0 &&
            filteredFiles.length === 0 && (
              <p className="px-4 py-5 text-sm text-gray-500">
                {search.trim()
                  ? 'No files match this filter.'
                  : 'This folder is empty.'}
              </p>
            )}
        </div>
        {listing.continuationToken && (
          <div className="border-t border-gray-200 p-4">
            <Button
              onClick={() => void loadMore()}
              text="Load more files"
              disabled={[
                {
                  disabled: isListing,
                  reason: 'Loading firmware files...',
                },
              ]}
            />
          </div>
        )}
      </section>
      {selectedFile && (
        <section
          aria-label="Selected firmware"
          className="rounded-lg border border-blue-200 bg-blue-50/50 p-4"
        >
          <div className="flex items-start gap-3">
            <FileIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="min-w-0">
              <h2 className="font-semibold text-gray-800">
                Selected firmware
              </h2>
              <p className="font-medium text-gray-700">
                {selectedFile.name}
              </p>
              <p className="break-all text-sm text-gray-500">
                {selectedFile.key}
              </p>
            </div>
          </div>
          {metadataMessage && (
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              {metadataMessage}
            </p>
          )}
          {inspection && (
            <FirmwareInspectionPanel inspection={inspection} />
          )}
          {hasMetadataOverride && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Warning: the entered FWM values override metadata parsed
              from the selected file.
            </p>
          )}
          <label>
            FWM Firmware Version Number (parsed and editable):
            <br />
            <input
              aria-label="Enter Firmware Version Number:"
              className="form-control block w-full mt-1 px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
              type="text"
              value={firmwareNumber}
              onChange={(event) =>
                setFirmwareNumber(event.target.value)
              }
            />
          </label>
          <label>
            FWM Semantic Firmware Version (parsed and editable):
            <br />
            <input
              aria-label="Enter Semantic Firmware Version:"
              className="form-control block w-full mt-1 px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
              type="text"
              value={firmwareSemantic}
              onChange={(event) =>
                setFirmwareSemantic(event.target.value)
              }
            />
          </label>
          {getFirmwareFamily({ productCode, variant }).length > 1 && (
            <div className="flex-row space-x-3">
              <input
                id="firmware-family-upload-from-s3"
                checked={useFirmwareFamily}
                type="checkbox"
                onChange={() =>
                  setUseFirmwareFamily(!useFirmwareFamily)
                }
              />
              <label htmlFor="firmware-family-upload-from-s3">
                Import to others in firmware family:{' '}
                {getFirmwareFamily({ productCode, variant })
                  .filter(
                    (item) =>
                      item.productCode !== productCode ||
                      item.variant !== variant,
                  )
                  .map(
                    (item) => `p${item.productCode}_v${item.variant}`,
                  )
                  .join(', ')}
              </label>
            </div>
          )}
          <Button
            onClick={() => void handleImport()}
            text="Import"
            disabled={[
              {
                disabled:
                  inputValidationFeedback() !== 'OK' ||
                  submissionStatus === 'success' ||
                  submissionStatus === 'fail',
                reason: inputValidationFeedback(),
              },
              { disabled: isImporting, reason: 'Working...' },
            ]}
          />
          <span role="status">{responseMessage}</span>
        </section>
      )}
    </div>
  );
};
